/**
 * NeoCoreUsuariosPage
 * ─────────────────────────────────────────────────────────────────
 * Gestión de usuarios STAFF de Neocoop (admin / contador / backoffice).
 * Solo accesible por `admin`. Está protegida por RoleGuard en App.tsx.
 *
 * Backend:
 *   - list_staff_users() RPC (SECURITY DEFINER, admin only)
 *   - edge function `admin-invite-staff` con 4 acciones:
 *       invite | reset | change_role | toggle_active
 *
 * Flujo UX:
 *   1. Tabla con búsqueda + filtro por rol
 *   2. Botón "Invitar usuario" → modal (email, nombre, rol) → devuelve
 *      password temporal para copiar UNA SOLA VEZ
 *   3. Fila con acciones: reset password, cambiar rol, activar/desactivar
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  UserCircleGear,
  UserPlus,
  MagnifyingGlass as Search,
  X,
  Copy,
  CheckCircle,
  Prohibit,
  Key,
  ShieldCheck,
  Calculator,
  Fingerprint,
  WarningCircle,
  ArrowsClockwise,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import type { StaffRole } from '@/lib/useRole'

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------

/**
 * Shape retornada por `list_staff_users()` (SECURITY DEFINER).
 * Definido en migración `0102_staff_roles_helpers.sql`.
 */
type StaffRow = {
  id: string
  email: string
  nombre: string
  role: StaffRole
  created_at: string | null
  last_sign_in_at: string | null
  bloqueado: boolean
}

type RoleFilter = 'todos' | StaffRole

// -----------------------------------------------------------------------------
// Helpers UI
// -----------------------------------------------------------------------------

const ROLE_META: Record<StaffRole, { label: string; Icon: PhosphorIcon; color: string }> = {
  admin:      { label: 'Admin',      Icon: ShieldCheck, color: '#dc2626' },
  contador:   { label: 'Contador',   Icon: Calculator,  color: '#0891b2' },
  backoffice: { label: 'Backoffice', Icon: Fingerprint, color: '#7c3aed' },
}

function RoleBadge({ role }: { role: StaffRole }) {
  const meta = ROLE_META[role]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: meta.color,
        background: `${meta.color}15`,
        border: `1px solid ${meta.color}30`,
      }}
    >
      <meta.Icon size={11} weight="bold" />
      {meta.label}
    </span>
  )
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: active ? '#059669' : '#dc2626',
        background: active ? '#ecfdf5' : '#fef2f2',
        border: `1px solid ${active ? '#10b98130' : '#f8717130'}`,
      }}
    >
      {active ? <CheckCircle size={11} weight="bold" /> : <Prohibit size={11} weight="bold" />}
      {active ? 'Activo' : 'Bloqueado'}
    </span>
  )
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60_000)
  if (m < 1) return 'ahora'
  if (m < 60) return `hace ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  if (d < 30) return `hace ${d} d`
  const meses = Math.floor(d / 30)
  return `hace ${meses} m`
}

// -----------------------------------------------------------------------------
// Componente principal
// -----------------------------------------------------------------------------

export function NeoCoreUsuariosPage() {
  const { session } = useAuth()
  const currentUserId = session?.user?.id ?? null

  const [rows, setRows] = useState<StaffRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('todos')

  const [showInvite, setShowInvite] = useState(false)
  const [tempCredentials, setTempCredentials] = useState<{
    email: string
    password: string
    role: StaffRole
    nombre?: string
  } | null>(null)

  // -------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------
  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase.rpc('list_staff_users')
    if (err) {
      setError(`No se pudo cargar la lista: ${err.message}`)
      setRows([])
    } else {
      setRows((data ?? []) as StaffRow[])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // -------------------------------------------------------------------
  // Derivados
  // -------------------------------------------------------------------
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase()
    return rows
      .filter((r) => (roleFilter === 'todos' ? true : r.role === roleFilter))
      .filter((r) => {
        if (!query) return true
        const bag = [r.email, r.nombre ?? '', r.role].join(' ').toLowerCase()
        return bag.includes(query)
      })
  }, [rows, q, roleFilter])

  const counts = useMemo(() => {
    const c = { total: rows.length, admin: 0, contador: 0, backoffice: 0, bloqueados: 0 }
    for (const r of rows) {
      if (r.role === 'admin' || r.role === 'contador' || r.role === 'backoffice') c[r.role]++
      if (r.bloqueado) c.bloqueados++
    }
    return c
  }, [rows])

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <UserCircleGear size={22} weight="regular" style={{ color: 'var(--brand-500, #FF5B04)' }} />
            <h2 style={{ margin: 0, fontSize: 20 }}>Usuarios staff</h2>
          </div>
          <p style={{ margin: 0, color: 'var(--n-500)', fontSize: 13 }}>
            Gestión de accesos de tu equipo interno: admin, contador y backoffice.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-ghost"
            onClick={refresh}
            title="Refrescar lista"
            style={{ gap: 6 }}
          >
            <ArrowsClockwise size={14} weight="bold" />
            Refrescar
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowInvite(true)}
            style={{ gap: 6 }}
          >
            <UserPlus size={14} weight="bold" />
            Invitar usuario
          </button>
        </div>
      </div>

      {/* Métricas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total staff" value={counts.total} />
        <StatCard label="Admins" value={counts.admin} tone="danger" />
        <StatCard label="Contadores" value={counts.contador} tone="info" />
        <StatCard label="Backoffice" value={counts.backoffice} tone="brand" />
        <StatCard label="Bloqueados" value={counts.bloqueados} tone="muted" />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <Search size={14} weight="bold" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--n-400)' }} />
          <input
            className="input"
            placeholder="Buscar por email, nombre…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ paddingLeft: 32, width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['todos', 'admin', 'contador', 'backoffice'] as RoleFilter[]).map((r) => (
            <button
              key={r}
              onClick={() => setRoleFilter(r)}
              className="btn"
              style={{
                padding: '6px 12px',
                fontSize: 12,
                background: roleFilter === r ? 'var(--brand-500, #FF5B04)' : 'transparent',
                color: roleFilter === r ? 'white' : 'var(--n-600)',
                border: '1px solid ' + (roleFilter === r ? 'transparent' : 'var(--n-200)'),
                textTransform: 'capitalize',
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      {error && (
        <div style={{ padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          <WarningCircle size={14} weight="bold" style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {error}
        </div>
      )}

      <div className="table-wrap" style={{ background: 'white', borderRadius: 10, border: '1px solid var(--n-200)' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--n-500)', fontSize: 13 }}>
            Cargando usuarios…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--n-500)' }}>
            <UserCircleGear size={32} weight="light" style={{ opacity: 0.5, marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              {rows.length === 0 ? 'No hay usuarios staff todavía' : 'Ningún usuario coincide con el filtro'}
            </div>
            {rows.length === 0 && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Tocá <strong>Invitar usuario</strong> arriba a la derecha para crear el primero.
              </div>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--n-50)', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--n-500)' }}>
                <th style={cellStyle}>Usuario</th>
                <th style={cellStyle}>Rol</th>
                <th style={cellStyle}>Estado</th>
                <th style={cellStyle}>Último login</th>
                <th style={{ ...cellStyle, textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <UserRow
                  key={row.id}
                  row={row}
                  isSelf={row.id === currentUserId}
                  onCredentials={(creds) => setTempCredentials(creds)}
                  onChanged={refresh}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modales */}
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onDone={(creds) => {
            setShowInvite(false)
            setTempCredentials(creds)
            refresh()
          }}
        />
      )}

      {tempCredentials && (
        <CredentialsModal
          data={tempCredentials}
          onClose={() => setTempCredentials(null)}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Fila individual con menú de acciones
// -----------------------------------------------------------------------------

function UserRow({
  row,
  isSelf,
  onCredentials,
  onChanged,
}: {
  row: StaffRow
  isSelf: boolean
  onCredentials: (c: { email: string; password: string; role: StaffRole; nombre?: string }) => void
  onChanged: () => void
}) {
  const [busy, setBusy] = useState<null | 'reset' | 'role' | 'toggle'>(null)
  const [changingRole, setChangingRole] = useState(false)
  const [newRole, setNewRole] = useState<StaffRole>(row.role)
  const [rowError, setRowError] = useState<string | null>(null)

  const callFn = async <T,>(body: Record<string, unknown>): Promise<T> => {
    const { data, error } = await supabase.functions.invoke('admin-invite-staff', { body })
    if (error) throw new Error(error.message)
    const payload = data as { error?: string } & T
    if (payload && (payload as { error?: string }).error) {
      throw new Error((payload as { error?: string }).error)
    }
    return data as T
  }

  const handleReset = async () => {
    if (!confirm(`¿Generar nueva contraseña temporal para ${row.email}?`)) return
    setBusy('reset')
    setRowError(null)
    try {
      const res = await callFn<{ password: string }>({ action: 'reset', user_id: row.id })
      onCredentials({
        email: row.email,
        password: res.password,
        role: row.role,
        nombre: row.nombre || undefined,
      })
    } catch (e) {
      setRowError((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const handleChangeRole = async () => {
    if (newRole === row.role) {
      setChangingRole(false)
      return
    }
    setBusy('role')
    setRowError(null)
    try {
      await callFn({ action: 'change_role', user_id: row.id, role: newRole })
      setChangingRole(false)
      onChanged()
    } catch (e) {
      setRowError((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const handleToggle = async () => {
    const activate = row.bloqueado // si está bloqueado, "activate" (unban)
    const verb = activate ? 'reactivar' : 'bloquear'
    if (!confirm(`¿Seguro que querés ${verb} a ${row.email}?`)) return
    setBusy('toggle')
    setRowError(null)
    try {
      await callFn({ action: 'toggle_active', user_id: row.id, activate })
      onChanged()
    } catch (e) {
      setRowError((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <tr style={{ borderTop: '1px solid var(--n-100)' }}>
      <td style={cellStyle}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {row.nombre || row.email.split('@')[0]}
            {isSelf && (
              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--n-500)', fontWeight: 500 }}>
                (vos)
              </span>
            )}
          </span>
          <span style={{ fontSize: 11, color: 'var(--n-500)' }}>{row.email}</span>
          {rowError && (
            <span style={{ fontSize: 11, color: '#b91c1c', marginTop: 3 }}>
              {rowError}
            </span>
          )}
        </div>
      </td>
      <td style={cellStyle}>
        {changingRole ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as StaffRole)}
              style={{ padding: '4px 6px', fontSize: 12, borderRadius: 6, border: '1px solid var(--n-300)' }}
              disabled={busy === 'role'}
            >
              <option value="admin">Admin</option>
              <option value="contador">Contador</option>
              <option value="backoffice">Backoffice</option>
            </select>
            <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: 11 }} onClick={handleChangeRole} disabled={busy === 'role'}>
              {busy === 'role' ? '…' : 'OK'}
            </button>
            <button className="btn btn-ghost" style={{ padding: '4px 6px', fontSize: 11 }} onClick={() => { setChangingRole(false); setNewRole(row.role) }}>
              ✕
            </button>
          </div>
        ) : (
          <RoleBadge role={row.role} />
        )}
      </td>
      <td style={cellStyle}>
        <ActiveBadge active={!row.bloqueado} />
      </td>
      <td style={{ ...cellStyle, color: 'var(--n-500)', fontSize: 12 }}>{timeAgo(row.last_sign_in_at)}</td>
      <td style={{ ...cellStyle, textAlign: 'right' }}>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button
            className="btn btn-ghost"
            title="Nueva contraseña temporal"
            onClick={handleReset}
            disabled={busy !== null}
            style={{ padding: '4px 8px', fontSize: 11, gap: 4 }}
          >
            <Key size={12} weight="bold" /> {busy === 'reset' ? '…' : 'Reset'}
          </button>
          {!changingRole && (
            <button
              className="btn btn-ghost"
              title="Cambiar rol"
              onClick={() => { setChangingRole(true); setNewRole(row.role) }}
              disabled={busy !== null}
              style={{ padding: '4px 8px', fontSize: 11, gap: 4 }}
            >
              <UserCircleGear size={12} weight="bold" /> Rol
            </button>
          )}
          <button
            className="btn btn-ghost"
            title={row.bloqueado ? 'Reactivar' : 'Bloquear'}
            onClick={handleToggle}
            disabled={busy !== null || isSelf}
            style={{
              padding: '4px 8px',
              fontSize: 11,
              gap: 4,
              color: row.bloqueado ? '#059669' : '#b91c1c',
            }}
          >
            {row.bloqueado ? <CheckCircle size={12} weight="bold" /> : <Prohibit size={12} weight="bold" />}
            {busy === 'toggle' ? '…' : row.bloqueado ? 'Reactivar' : 'Bloquear'}
          </button>
        </div>
      </td>
    </tr>
  )
}

// -----------------------------------------------------------------------------
// Modal · Invitar usuario
// -----------------------------------------------------------------------------

function InviteModal({
  onClose,
  onDone,
}: {
  onClose: () => void
  onDone: (c: { email: string; password: string; role: StaffRole; nombre?: string }) => void
}) {
  const [email, setEmail] = useState('')
  const [nombre, setNombre] = useState('')
  const [role, setRole] = useState<StaffRole>('backoffice')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    const emailNorm = email.trim().toLowerCase()
    if (!emailNorm || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailNorm)) {
      setError('Email inválido')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('admin-invite-staff', {
        body: { action: 'invite', email: emailNorm, role, nombre: nombre.trim() },
      })
      if (err) throw new Error(err.message)
      const payload = data as { error?: string; password?: string; email?: string }
      if (payload.error) throw new Error(payload.error)
      if (!payload.password) throw new Error('No se recibió password temporal')
      onDone({
        email: payload.email ?? emailNorm,
        password: payload.password,
        role,
        nombre: nombre.trim() || undefined,
      })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell title="Invitar usuario staff" onClose={onClose}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input
            className="input"
            type="email"
            placeholder="nombre@neocoop.gt"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label style={labelStyle}>Nombre completo (opcional)</label>
          <input
            className="input"
            placeholder="Ej: Nataly Pérez"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label style={labelStyle}>Rol</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {(['admin', 'contador', 'backoffice'] as StaffRole[]).map((r) => {
              const meta = ROLE_META[r]
              const active = role === r
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    border: `1px solid ${active ? meta.color : 'var(--n-200)'}`,
                    background: active ? `${meta.color}10` : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                  }}
                >
                  <meta.Icon size={18} weight="regular" style={{ color: meta.color }} />
                  <span style={{ fontWeight: 600 }}>{meta.label}</span>
                  <span style={{ fontSize: 10, color: 'var(--n-500)', textAlign: 'center', lineHeight: 1.3 }}>
                    {r === 'admin' ? 'Todo' : r === 'contador' ? 'Contabilidad' : 'Operativo'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {error && (
          <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !email.trim()}>
            {busy ? 'Creando…' : 'Crear usuario'}
          </button>
        </div>
      </div>
    </ModalShell>
  )
}

// -----------------------------------------------------------------------------
// Modal · Mostrar credenciales UNA VEZ para copiar
// -----------------------------------------------------------------------------

function CredentialsModal({
  data,
  onClose,
}: {
  data: { email: string; password: string; role: StaffRole; nombre?: string }
  onClose: () => void
}) {
  const [copied, setCopied] = useState<'email' | 'password' | 'both' | null>(null)

  const copy = async (text: string, kind: 'email' | 'password' | 'both') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(kind)
      setTimeout(() => setCopied(null), 1800)
    } catch {
      /* ignore */
    }
  }

  const bothText = `Neocoop · acceso a NeoCore
Email: ${data.email}
Password temporal: ${data.password}

Entrás una sola vez con esto. Al primer login te va a pedir cambiar la contraseña por una tuya.`

  return (
    <ModalShell title="Credenciales temporales" onClose={onClose}>
      <div style={{ padding: '4px 0 8px' }}>
        <div style={{ display: 'flex', gap: 10, background: '#fef3c7', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#92400e' }}>
          <WarningCircle size={18} weight="bold" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Copiala ahora.</strong> Esta contraseña temporal no se puede volver a mostrar.
            El usuario la cambia al primer login.
          </div>
        </div>

        <FieldRow label="Email" value={data.email} onCopy={() => copy(data.email, 'email')} copied={copied === 'email'} />
        <FieldRow label="Password temporal" value={data.password} mono onCopy={() => copy(data.password, 'password')} copied={copied === 'password'} />
        <FieldRow label="Rol asignado" value={ROLE_META[data.role].label} />

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="btn btn-ghost" style={{ gap: 6 }} onClick={() => copy(bothText, 'both')}>
            <Copy size={13} weight="bold" />
            {copied === 'both' ? 'Copiado' : 'Copiar mensaje completo'}
          </button>
          <button className="btn btn-primary" onClick={onClose}>Listo</button>
        </div>
      </div>
    </ModalShell>
  )
}

function FieldRow({
  label,
  value,
  mono,
  onCopy,
  copied,
}: {
  label: string
  value: string
  mono?: boolean
  onCopy?: () => void
  copied?: boolean
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--n-500)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div
          style={{
            flex: 1,
            padding: '8px 12px',
            background: 'var(--n-50)',
            border: '1px solid var(--n-200)',
            borderRadius: 6,
            fontSize: mono ? 14 : 13,
            fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
            fontWeight: mono ? 600 : 400,
            letterSpacing: mono ? 0.5 : 0,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </div>
        {onCopy && (
          <button
            className="btn btn-ghost"
            onClick={onCopy}
            style={{ padding: '6px 10px', fontSize: 11, gap: 4 }}
            title="Copiar"
          >
            {copied ? <CheckCircle size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        )}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Shell reutilizable de modal
// -----------------------------------------------------------------------------

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string
  children: React.ReactNode
  onClose: () => void
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: '1px solid var(--n-100)',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: 4 }} aria-label="Cerrar">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div style={{ padding: 18 }}>{children}</div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// StatCard
// -----------------------------------------------------------------------------

function StatCard({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: number
  tone?: 'neutral' | 'brand' | 'danger' | 'info' | 'muted'
}) {
  const palette = {
    neutral: { color: 'var(--n-800)', bg: 'white' },
    brand:   { color: '#7c3aed',    bg: '#faf5ff' },
    danger:  { color: '#dc2626',    bg: '#fef2f2' },
    info:    { color: '#0891b2',    bg: '#ecfeff' },
    muted:   { color: 'var(--n-500)', bg: 'var(--n-50)' },
  }[tone]

  return (
    <div style={{ background: palette.bg, padding: 12, borderRadius: 10, border: '1px solid var(--n-100)' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--n-500)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: palette.color, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Estilos comunes
// -----------------------------------------------------------------------------

const cellStyle: React.CSSProperties = {
  padding: '10px 14px',
  verticalAlign: 'middle',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--n-500)',
  marginBottom: 4,
  fontWeight: 600,
}
